import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

export const fetchEmployees = createAsyncThunk(
  "employee/fetchEmployees",
  async (filters = {}, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().auth;
      
      const { 
        companyId, 
        branchId, 
        roleId, 
        page, 
        limit,
        search,       
        department,   
        status,       
        assignmentType 
      } = filters;

      const params = {};

      // 1. Map structural filters
      if (branchId && branchId !== 'All') params.branchId = branchId;
      if (roleId && roleId !== 'All') params.roleId = roleId;

      // 2. Map text search filters (sending both 'search' and 'name' to cover backend expectations)
      if (search && search.trim() !== "") {
        params.search = search.trim();
        params.name = search.trim();
      }
      if (department && department.trim() !== "") params.department = department.trim();

      // 3. Map dropdown filters
      if (status && status !== "All") {
        params.status = status;
      }

      if (assignmentType && assignmentType.toLowerCase() !== "both" && assignmentType !== "All") {
        params.assignmentType = assignmentType;
      }

      // 4. Setup pagination defaults
      params.page = page || 1;
      params.limit = limit || 10;

      let url = `${API_URL}/employee`;
      if (companyId) {
        url = `${API_URL}/employee/by-company/${companyId}`;
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      return response.data;
    } catch (err) {
      console.log(err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch employees";
      return rejectWithValue(errorMessage);
    }
  },
);

const employeeSlice = createSlice({
  name: "employee",
  initialState: {
    list: [],
    meta: {},
    status: "idle",
    error: null,
  },
  reducers: {
    addEmployee: (state, action) => {
      state.list.unshift(action.payload);
    },
    updateEmployeeInList: (state, action) => {
      const index = state.list.findIndex((emp) => emp.id === action.payload.id);
      if (index !== -1) {
        state.list[index] = { ...state.list[index], ...action.payload };
      }
    },
    removeEmployeeFromList: (state, action) => {
      state.list = state.list.filter((emp) => emp.id !== action.payload);
    },
    clearEmployeeError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload.data || [];
        state.meta = action.payload.meta || {};
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const {
  addEmployee,
  updateEmployeeInList,
  removeEmployeeFromList,
  clearEmployeeError,
} = employeeSlice.actions;

export default employeeSlice.reducer;